import React, { useState, useRef, useEffect, useCallback } from "react";
import { useNode, useEditor } from "@craftjs/core";
import { FormControl, FormLabel, TextField, Select, MenuItem, Checkbox, FormControlLabel } from "@mui/material";
import { useTextPersistence } from "qwanyx";

export const ImageInput = ({ 
  src = "https://picsum.photos/400/300",
  frameWidth = "300px",
  aspectRatio = "4/3",
  objectFit = "contain",
  borderWidth = "1px",
  borderColor = "#ddd",
  borderRadius = "0px",
  pictureX = 0,
  pictureY = 0,
  pictureRotation = 0,
  pictureScale = 1,
  pictureFlipH = false,
  pictureFlipV = false,
  showBackground = true,
  backgroundColor = "#f9f9f9",
  hugImage = false,
  qwantumId,
  onSave,
  style,
  ...props
}) => {
  const { 
    connectors: { connect, drag },
    isSelected,
    isHovered
  } = useNode((state) => ({
    isSelected: state.events.selected,
    isHovered: state.events.hovered,
  }));

  const { enabled } = useEditor((state) => ({
    enabled: state.options.enabled
  }));

  const { text, handleBlur: persistenceBlur } = useTextPersistence(qwantumId, onSave);
  
  // Current editing state for immediate feedback
  const [currentUrl, setCurrentUrl] = useState(src);
  const [currentTransforms, setCurrentTransforms] = useState({ tx: pictureX, ty: pictureY, r: pictureRotation, scale: pictureScale, flipx: pictureFlipH, flipy: pictureFlipV });
  const [prevData, setPrevData] = useState(text || "");
  const [isEditing, setIsEditing] = useState(false);
  const [initialState, setInitialState] = useState(null);
  const [modalPosition, setModalPosition] = useState({ x: 100, y: 100 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 });
  const [originalDimensions, setOriginalDimensions] = useState({ width: 0, height: 0 });
  const [displayDimensions, setDisplayDimensions] = useState({ width: '', height: '' });

  // Update local state when persisted data changes
  useEffect(() => {
    if (text) {
      try {
        const parsed = JSON.parse(text);
        setCurrentUrl(parsed.url || src);
        setCurrentTransforms(parsed.transforms || { tx: pictureX, ty: pictureY, r: pictureRotation, scale: pictureScale, flipx: pictureFlipH, flipy: pictureFlipV });
      } catch (e) {
        // Use defaults if JSON parse fails
        setCurrentUrl(src);
        setCurrentTransforms({ tx: pictureX, ty: pictureY, r: pictureRotation, scale: pictureScale, flipx: pictureFlipH, flipy: pictureFlipV });
      }
    } else {
      setCurrentUrl(src);
      setCurrentTransforms({ tx: pictureX, ty: pictureY, r: pictureRotation, scale: pictureScale, flipx: pictureFlipH, flipy: pictureFlipV });
    }
  }, [text]); // Removed all the other dependencies

  const persistData = (url, transforms) => {
    const newData = { url, transforms };
    const dataString = JSON.stringify(newData);
    if (dataString !== prevData) {
      const syntheticEvent = {
        target: { value: dataString }
      };
      persistenceBlur(syntheticEvent);
      if (onSave) {
        onSave(dataString);
      }
      setPrevData(dataString);
    }
  };

  const handleUrlChange = (newUrl) => {
    setCurrentUrl(newUrl);
  };

  const handleTransformChange = (key, value) => {
    setCurrentTransforms(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleReset = () => {
    // Reset transforms and dimensions but keep current URL
    setCurrentTransforms({ tx: pictureX, ty: pictureY, r: pictureRotation, scale: pictureScale, flipx: pictureFlipH, flipy: pictureFlipV });
    setImageDimensions(originalDimensions);
    setDisplayDimensions({ width: originalDimensions.width.toString(), height: originalDimensions.height.toString() });
  };

  const handleCancel = () => {
    // Restore everything to initial state
    if (initialState) {
      setCurrentUrl(initialState.url);
      setCurrentTransforms(initialState.transforms);
      setImageDimensions(initialState.imageDimensions);
      setDisplayDimensions(initialState.displayDimensions);
    }
    setIsEditing(false);
  };

  const handleOpenEdit = () => {
    // Save initial state before opening editor
    setInitialState({
      url: currentUrl,
      transforms: { ...currentTransforms },
      imageDimensions: { ...imageDimensions },
      displayDimensions: { ...displayDimensions }
    });
    setIsEditing(true);
  };

  const handleImageLoad = (e) => {
    const img = e.target;
    const dims = { width: img.naturalWidth, height: img.naturalHeight };
    setImageDimensions(dims);
    setOriginalDimensions(dims);
    setDisplayDimensions({ width: dims.width.toString(), height: dims.height.toString() });
  };

  const handleDimensionChange = (dimension, value) => {
    const numValue = parseInt(value) || 0;
    if (dimension === 'width' && originalDimensions.width > 0) {
      const aspectRatio = originalDimensions.height / originalDimensions.width;
      const newHeight = Math.round(numValue * aspectRatio);
      setDisplayDimensions({ width: value, height: newHeight.toString() });
    } else if (dimension === 'height' && originalDimensions.height > 0) {
      const aspectRatio = originalDimensions.width / originalDimensions.height;
      const newWidth = Math.round(numValue * aspectRatio);
      setDisplayDimensions({ width: newWidth.toString(), height: value });
    }
  };

  const handleResize = () => {
    const newWidth = parseInt(displayDimensions.width) || imageDimensions.width;
    const newHeight = parseInt(displayDimensions.height) || imageDimensions.height;
    
    if (newWidth && newHeight && imageDimensions.width && imageDimensions.height) {
      // Create a canvas to resize the image
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      canvas.width = newWidth;
      canvas.height = newHeight;
      
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        ctx.drawImage(img, 0, 0, newWidth, newHeight);
        const resizedDataUrl = canvas.toDataURL('image/jpeg', 0.9);
        setCurrentUrl(resizedDataUrl);
        setImageDimensions({ width: newWidth, height: newHeight });
      };
      img.src = currentUrl;
    }
  };

  const handleMouseDown = (e) => {
    if (e.target.dataset.dragHandle) {
      setIsDragging(true);
      setDragOffset({
        x: e.clientX - modalPosition.x,
        y: e.clientY - modalPosition.y
      });
    }
  };

  const handleMouseMove = useCallback((e) => {
    if (isDragging) {
      setModalPosition({
        x: e.clientX - dragOffset.x,
        y: e.clientY - dragOffset.y
      });
    }
  }, [isDragging, dragOffset]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const formatSize = (value) => {
    if (!value) return 'auto';
    if (typeof value === 'string') return value;
    return `${value}px`;
  };

  const formatRadius = (value) => {
    if (!value) return '0px';
    if (typeof value === 'string') return value;
    return `${value}px`;
  };

  const getSelectionStyle = () => {
    if (!enabled) return {};
    
    if (isSelected) {
      return {
        outline: '1px dashed #2196f3',
        outlineOffset: '2px'
      };
    }
    if (isHovered) {
      return {
        outline: '1px dashed #ff9800',
        outlineOffset: '1px'
      };
    }
    return {};
  };

  const pictureTransform = `
    translate(${currentTransforms.tx}px, ${currentTransforms.ty}px)
    rotate(${currentTransforms.r}deg)
    scale(${currentTransforms.scale * (currentTransforms.flipx ? -1 : 1)}, ${currentTransforms.scale * (currentTransforms.flipy ? -1 : 1)})
  `;

  return (
    <>
      <div 
        ref={ref => enabled ? connect(drag(ref)) : connect(ref)}
        style={{ 
          width: formatSize(frameWidth),
          ...(hugImage ? {} : { aspectRatio: aspectRatio }),
          border: `${borderWidth} solid ${borderColor}`,
          borderRadius: formatRadius(borderRadius),
          overflow: 'hidden',
          position: 'relative',
          backgroundColor: showBackground ? backgroundColor : 'transparent',
          transition: 'all 0.2s ease',
          ...style,
          ...getSelectionStyle()
        }}
        {...props}
      >
        <img 
          src={currentUrl}
          alt="Component image"
          draggable={false}
          onClick={handleOpenEdit}
          onLoad={handleImageLoad}
          style={{ 
            width: '100%', 
            height: hugImage ? 'auto' : '100%', 
            objectFit: hugImage ? 'none' : objectFit,
            display: 'block',
            transform: pictureTransform,
            transformOrigin: 'center center',
            cursor: 'pointer'
          }}
        />
      </div>

      {/* Edit Modal */}
      {isEditing && (
        <div
          style={{
            position: 'fixed',
            left: `${modalPosition.x}px`,
            top: `${modalPosition.y}px`,
            zIndex: 1000,
            backgroundColor: 'white',
            padding: '12px',
            borderRadius: '8px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
            width: '280px',
            border: '2px solid #2196f3',
            cursor: isDragging ? 'grabbing' : 'default'
          }}
          onMouseDown={handleMouseDown}
        >
          <div 
            data-drag-handle="true"
            style={{
              padding: '8px',
              backgroundColor: '#f5f5f5',
              margin: '-12px -12px 8px -12px',
              borderRadius: '6px 6px 0 0',
              cursor: 'grab',
              textAlign: 'center',
              fontSize: '14px',
              fontWeight: 'bold',
              color: '#666',
              userSelect: 'none'
            }}
          >
            Edit Image ⋮⋮
          </div>
          
          <div style={{ marginBottom: '8px' }}>
            <label style={{ display: 'block', marginBottom: '2px', fontSize: '11px', fontWeight: 'bold' }}>URL:</label>
            <input
              type="text"
              value={currentUrl}
              onChange={(e) => handleUrlChange(e.target.value)}
              placeholder="Image URL"
              style={{
                width: '100%',
                padding: '4px',
                fontSize: '12px',
                border: '1px solid #ccc',
                borderRadius: '3px'
              }}
            />
          </div>

          {originalDimensions.width > 0 && (
            <div style={{ marginBottom: '8px', padding: '6px', backgroundColor: '#f9f9f9', borderRadius: '3px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '10px', fontWeight: 'bold' }}>
                Original: {originalDimensions.width} × {originalDimensions.height}px
              </label>
              <div style={{ display: 'flex', gap: '4px', alignItems: 'center', marginBottom: '4px' }}>
                <input
                  type="number"
                  value={displayDimensions.width}
                  onChange={(e) => handleDimensionChange('width', e.target.value)}
                  disabled={true}
                  placeholder="Width"
                  style={{ width: '60px', padding: '2px', fontSize: '10px', border: '1px solid #ccc', borderRadius: '2px' }}
                />
                <span style={{ fontSize: '10px' }}>×</span>
                <input
                  type="number"
                  value={displayDimensions.height}
                  onChange={(e) => handleDimensionChange('height', e.target.value)}
                  disabled={true}
                  placeholder="Height"
                  style={{ width: '60px', padding: '2px', fontSize: '10px', border: '1px solid #ccc', borderRadius: '2px' }}
                />
                <button
                  onClick={handleResize}
                  disabled={true}
                  style={{
                    padding: '2px 6px',
                    fontSize: '9px',
                    backgroundColor: '#ff9800',
                    color: 'white',
                    border: 'none',
                    borderRadius: '2px',
                    cursor: 'pointer'
                  }}
                >
                  Resize
                </button>
              </div>
              <div style={{ display: 'flex', gap: '2px' }}>
                {[75, 50, 25].map(percent => (
                  <button
                    key={percent}
                    onClick={() => {
                      const newWidth = Math.round(originalDimensions.width * percent / 100);
                      const newHeight = Math.round(originalDimensions.height * percent / 100);
                      setDisplayDimensions({ width: newWidth.toString(), height: newHeight.toString() });
                    }}
                    disabled={true}
                    style={{
                      padding: '1px 4px',
                      fontSize: '8px',
                      backgroundColor: '#e0e0e0',
                      border: 'none',
                      borderRadius: '2px',
                      cursor: 'pointer'
                    }}
                  >
                    {percent}%
                  </button>
                ))}
              </div>
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', marginBottom: '8px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '2px', fontSize: '10px', fontWeight: 'bold' }}>X:</label>
              <input
                type="number"
                value={currentTransforms.tx}
                onChange={(e) => handleTransformChange('tx', parseInt(e.target.value) || 0)}
                style={{ width: '100%', padding: '3px', fontSize: '11px', border: '1px solid #ccc', borderRadius: '2px' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '2px', fontSize: '10px', fontWeight: 'bold' }}>Y:</label>
              <input
                type="number"
                value={currentTransforms.ty}
                onChange={(e) => handleTransformChange('ty', parseInt(e.target.value) || 0)}
                style={{ width: '100%', padding: '3px', fontSize: '11px', border: '1px solid #ccc', borderRadius: '2px' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '2px', fontSize: '10px', fontWeight: 'bold' }}>Rotate:</label>
              <input
                type="number"
                value={currentTransforms.r}
                onChange={(e) => handleTransformChange('r', parseInt(e.target.value) || 0)}
                style={{ width: '100%', padding: '3px', fontSize: '11px', border: '1px solid #ccc', borderRadius: '2px' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '2px', fontSize: '10px', fontWeight: 'bold' }}>Scale:</label>
              <input
                type="number"
                step="0.1"
                value={currentTransforms.scale}
                onChange={(e) => handleTransformChange('scale', parseFloat(e.target.value) || 1)}
                style={{ width: '100%', padding: '3px', fontSize: '11px', border: '1px solid #ccc', borderRadius: '2px' }}
              />
            </div>
          </div>

          <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
            <label style={{ display: 'flex', alignItems: 'center', fontSize: '11px' }}>
              <input
                type="checkbox"
                checked={currentTransforms.flipx}
                onChange={(e) => handleTransformChange('flipx', e.target.checked)}
                style={{ marginRight: '4px' }}
              />
              Flip H
            </label>
            <label style={{ display: 'flex', alignItems: 'center', fontSize: '11px' }}>
              <input
                type="checkbox"
                checked={currentTransforms.flipy}
                onChange={(e) => handleTransformChange('flipy', e.target.checked)}
                style={{ marginRight: '4px' }}
              />
              Flip V
            </label>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '4px' }}>
            <button
              onClick={handleReset}
              style={{
                padding: '6px 8px',
                backgroundColor: '#ff9800',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '11px',
                flex: 1
              }}
            >
              Reset
            </button>
            <button
              onClick={handleCancel}
              style={{
                padding: '6px 8px',
                backgroundColor: '#f44336',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '11px',
                flex: 1
              }}
            >
              Cancel
            </button>
            <button
              onClick={() => {
                // Persist data when closing
                persistData(currentUrl, currentTransforms);
                setIsEditing(false);
              }}
              style={{
                padding: '6px 8px',
                backgroundColor: '#2196f3',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '11px',
                flex: 1
              }}
            >
              Done
            </button>
          </div>
        </div>
      )}
    </>
  );
};

const ImageInputSettings = () => {
  const { 
    actions: { setProp },
    frameWidth, aspectRatio, objectFit,
    borderWidth, borderColor, borderRadius, qwantumId,
    showBackground, backgroundColor, hugImage
  } = useNode((node) => ({
    frameWidth: node.data.props.frameWidth,
    aspectRatio: node.data.props.aspectRatio,
    objectFit: node.data.props.objectFit,
    borderWidth: node.data.props.borderWidth,
    borderColor: node.data.props.borderColor,
    borderRadius: node.data.props.borderRadius,
    qwantumId: node.data.props.qwantumId,
    showBackground: node.data.props.showBackground,
    backgroundColor: node.data.props.backgroundColor,
    hugImage: node.data.props.hugImage
  }));

  const [localFrameWidth, setLocalFrameWidth] = React.useState(frameWidth);
  const [localBorderRadius, setLocalBorderRadius] = React.useState(borderRadius);
  const [localBorderWidth, setLocalBorderWidth] = React.useState(borderWidth);

  React.useEffect(() => setLocalFrameWidth(frameWidth), [frameWidth]);
  React.useEffect(() => setLocalBorderRadius(borderRadius), [borderRadius]);
  React.useEffect(() => setLocalBorderWidth(borderWidth), [borderWidth]);

  const handleSizeChange = (prop, value) => {
    if (value && !value.includes('%') && !value.includes('px') && !value.includes('rem') && !value.includes('em')) {
      value = `${value}px`;
    }
    setProp(props => props[prop] = value);
  };

  return (
    <div>
      <FormControl fullWidth margin="normal">
        <FormLabel>Quantum ID</FormLabel>
        <TextField
          value={qwantumId || ''}
          onChange={(e) => setProp(props => props.qwantumId = e.target.value)}
          size="small"
          placeholder="Enter quantum ID for persistence"
        />
      </FormControl>

      <FormControl fullWidth margin="normal">
        <FormLabel>Frame Width</FormLabel>
        <TextField
          value={localFrameWidth}
          onChange={(e) => setLocalFrameWidth(e.target.value)}
          onBlur={(e) => handleSizeChange('frameWidth', e.target.value)}
          size="small"
          placeholder="300px, 100%"
        />
      </FormControl>

      <FormControlLabel
        control={
          <Checkbox
            checked={hugImage}
            onChange={(e) => setProp(props => props.hugImage = e.target.checked)}
          />
        }
        label="Hug Image (Natural Ratio)"
      />

      {!hugImage && (
        <FormControl fullWidth margin="normal">
          <FormLabel>Aspect Ratio</FormLabel>
          <Select
            value={aspectRatio || '4/3'}
            onChange={(e) => setProp(props => props.aspectRatio = e.target.value)}
            size="small"
          >
            <MenuItem value="1/1">Square (1:1)</MenuItem>
            <MenuItem value="4/3">Standard (4:3)</MenuItem>
            <MenuItem value="3/4">Portrait (3:4)</MenuItem>
            <MenuItem value="3/2">Classic (3:2)</MenuItem>
            <MenuItem value="2/3">Portrait Classic (2:3)</MenuItem>
            <MenuItem value="16/9">Widescreen (16:9)</MenuItem>
            <MenuItem value="9/16">Vertical (9:16)</MenuItem>
            <MenuItem value="5/4">Large Format (5:4)</MenuItem>
            <MenuItem value="4/5">Portrait Large (4:5)</MenuItem>
          </Select>
        </FormControl>
      )}

      {!hugImage && (
        <FormControl fullWidth margin="normal">
          <FormLabel>Image Fit</FormLabel>
          <Select
            value={objectFit || 'contain'}
            onChange={(e) => setProp(props => props.objectFit = e.target.value)}
            size="small"
          >
            <MenuItem value="contain">Contain</MenuItem>
            <MenuItem value="cover">Cover</MenuItem>
            <MenuItem value="fill">Fill</MenuItem>
          </Select>
        </FormControl>
      )}

      <FormControlLabel
        control={
          <Checkbox
            checked={showBackground}
            onChange={(e) => setProp(props => props.showBackground = e.target.checked)}
          />
        }
        label="Show Background"
      />

      {showBackground && (
        <FormControl fullWidth margin="normal" component="fieldset">
          <FormLabel component="legend">Background Color</FormLabel>
          <input
            type="color"
            value={backgroundColor || '#f9f9f9'}
            onChange={(e) => setProp(props => props.backgroundColor = e.target.value)}
            style={{ width: '100%', height: '40px', border: 'none', borderRadius: '4px' }}
          />
        </FormControl>
      )}

      <FormControl fullWidth margin="normal">
        <FormLabel>Border Width</FormLabel>
        <TextField
          value={localBorderWidth}
          onChange={(e) => setLocalBorderWidth(e.target.value)}
          onBlur={(e) => handleSizeChange('borderWidth', e.target.value)}
          size="small"
          placeholder="0px, 1px, 2px, 4px"
        />
      </FormControl>

      <FormControl fullWidth margin="normal" component="fieldset">
        <FormLabel component="legend">Border Color</FormLabel>
        <input
          type="color"
          value={borderColor || '#dddddd'}
          onChange={(e) => setProp(props => props.borderColor = e.target.value)}
          style={{ width: '100%', height: '40px', border: 'none', borderRadius: '4px' }}
        />
      </FormControl>

      <FormControl fullWidth margin="normal">
        <FormLabel>Border Radius</FormLabel>
        <TextField
          value={localBorderRadius}
          onChange={(e) => setLocalBorderRadius(e.target.value)}
          onBlur={(e) => handleSizeChange('borderRadius', e.target.value)}
          size="small"
          placeholder="0px, 8px, 1rem"
        />
      </FormControl>
    </div>
  );
};

ImageInput.craft = {
  props: {
    src: "https://picsum.photos/400/300",
    frameWidth: "300px",
    aspectRatio: "4/3",
    objectFit: "contain",
    borderWidth: "1px",
    borderColor: "#ddd",
    borderRadius: "0px",
    pictureX: 0,
    pictureY: 0,
    pictureRotation: 0,
    pictureScale: 1,
    pictureFlipH: false,
    pictureFlipV: false,
    showBackground: true,
    backgroundColor: "#f9f9f9",
    hugImage: false,
    qwantumId: "",
    onSave: null
  },
  related: {
    settings: ImageInputSettings
  }
};